import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { EXIF_FIELDS } from '../config/fieldDefinitions';
import {
  parseFiniteCoordinate,
  quantizeGpsPairToNearestMeter,
} from '../utils/gpsPrecision';

const STORAGE_KEY = 'exiftrator-presets-v3';
const V2_STORAGE_KEY = 'exiftrator-presets-v2';
const OLD_STORAGE_KEY = 'exiftrator-presets';

const PresetsContext = createContext();

function includesGpsField(includedFields = []) {
  return includedFields.includes('latitude') || includedFields.includes('longitude');
}

function normalizePresetValues(includedFields = [], values = {}) {
  if (!includesGpsField(includedFields) || !values || typeof values !== 'object') {
    return values;
  }

  const hasLatitude = Object.prototype.hasOwnProperty.call(values, 'latitude');
  const hasLongitude = Object.prototype.hasOwnProperty.call(values, 'longitude');
  const latitude = hasLatitude ? parseFiniteCoordinate(values.latitude) : null;
  const longitude = hasLongitude ? parseFiniteCoordinate(values.longitude) : null;

  if (!hasLatitude && !hasLongitude) {
    return values;
  }

  const quantized = quantizeGpsPairToNearestMeter(latitude, longitude);

  return {
    ...values,
    ...(hasLatitude && latitude !== null ? { latitude: quantized.latitude } : {}),
    ...(hasLongitude && longitude !== null ? { longitude: quantized.longitude } : {}),
  };
}

function normalizePreset(preset) {
  const includedFields = Array.isArray(preset?.includedFields) ? preset.includedFields : [];
  return {
    ...preset,
    includedFields,
    values: normalizePresetValues(includedFields, preset?.values || {}),
  };
}

/**
 * Migrate presets from v2 (grouped by type) to v3 (flat array with includedFields)
 * @param {Object} v2Data - V2 preset data grouped by type
 * @returns {Array} Flat array of presets
 */
function migrateV2ToV3(v2Data) {
  const migrated = [];

  // Migrate camera presets
  if (v2Data.camera && Array.isArray(v2Data.camera)) {
    v2Data.camera.forEach(preset => {
      migrated.push({
        id: preset.id || Date.now() + Math.random(),
        name: preset.name,
        includedFields: ['cameraMake', 'cameraModel'],
        values: {
          cameraMake: preset.values?.cameraMake || '',
          cameraModel: preset.values?.cameraModel || ''
        }
      });
    });
  }

  // Migrate lens presets
  if (v2Data.lens && Array.isArray(v2Data.lens)) {
    v2Data.lens.forEach(preset => {
      migrated.push({
        id: preset.id || Date.now() + Math.random(),
        name: preset.name,
        includedFields: ['lensMake', 'lensModel'],
        values: {
          lensMake: preset.values?.lensMake || '',
          lensModel: preset.values?.lensModel || ''
        }
      });
    });
  }

  // Migrate GPS presets
  if (v2Data.gps && Array.isArray(v2Data.gps)) {
    v2Data.gps.forEach(preset => {
      migrated.push({
        id: preset.id || Date.now() + Math.random(),
        name: preset.name,
        includedFields: ['latitude', 'longitude'],
        values: normalizePresetValues(['latitude', 'longitude'], {
          latitude: preset.values?.latitude,
          longitude: preset.values?.longitude
        })
      });
    });
  }

  return migrated;
}

export function PresetsProvider({ children }) {
  // Flat array of all presets
  const [presets, setPresets] = useState([]);

  // Load presets from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const normalized = Array.isArray(parsed) ? parsed.map(normalizePreset) : [];
        setPresets(normalized);

        if (JSON.stringify(parsed) !== JSON.stringify(normalized)) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
        }
      } else {
        // Try migrating from v2
        const v2Stored = localStorage.getItem(V2_STORAGE_KEY);
        if (v2Stored) {
          const v2Data = JSON.parse(v2Stored);
          const migrated = migrateV2ToV3(v2Data);
          if (migrated.length > 0) {
            setPresets(migrated);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
            console.log('Migrated presets from v2 to v3:', migrated);
          }
        } else {
          // Try migrating from old format
          const oldStored = localStorage.getItem(OLD_STORAGE_KEY);
          if (oldStored) {
            const oldData = JSON.parse(oldStored);
            // Old format was similar to v2, migrate through v2 structure
            const v2Data = {};
            if (oldData.camera) v2Data.camera = oldData.camera;
            if (oldData.gps) v2Data.gps = oldData.gps;
            if (oldData.lens) v2Data.lens = oldData.lens;

            const migrated = migrateV2ToV3(v2Data);
            if (migrated.length > 0) {
              setPresets(migrated);
              localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
              console.log('Migrated presets from old format to v3:', migrated);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  }, []);

  // Save to localStorage
  const saveToStorage = useCallback((newPresets) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newPresets));
    } catch (error) {
      console.error('Failed to save presets:', error);
    }
  }, []);

  // Add a new preset
  const addPreset = useCallback((name, includedFields, values) => {
    const newPreset = {
      id: Date.now(),
      name,
      includedFields,
      values: normalizePresetValues(includedFields, values)
    };

    setPresets(prev => {
      const updated = [...prev, newPreset];
      saveToStorage(updated);
      return updated;
    });

    return newPreset;
  }, [saveToStorage]);

  // Update a preset
  const updatePreset = useCallback((presetId, updates) => {
    setPresets(prev => {
      const updated = prev.map(p =>
        p.id === presetId
          ? normalizePreset({ ...p, ...updates })
          : p
      );
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  // Delete a preset
  const deletePreset = useCallback((presetId) => {
    setPresets(prev => {
      const updated = prev.filter(p => p.id !== presetId);
      saveToStorage(updated);
      return updated;
    });
  }, [saveToStorage]);

  // Get all presets
  const getAllPresets = useCallback(() => {
    return presets;
  }, [presets]);

  // Get a preset by ID
  const getPresetById = useCallback((presetId) => {
    return presets.find(p => p.id === presetId);
  }, [presets]);

  // Get presets that include specific fields
  const getPresetsForFields = useCallback((fieldIds) => {
    return presets.filter(preset =>
      preset.includedFields.some(field => fieldIds.includes(field))
    );
  }, [presets]);

  // Get presets for a specific category (legacy compatibility)
  const getPresetsForGroup = useCallback((groupId) => {
    const groupFieldMap = {
      camera: ['cameraMake', 'cameraModel'],
      lens: ['lensMake', 'lensModel'],
      gps: ['latitude', 'longitude']
    };
    const groupFields = groupFieldMap[groupId] || [];
    return presets.filter(preset =>
      preset.includedFields.some(field => groupFields.includes(field))
    );
  }, [presets]);

  // Check if current form values match a preset
  const findMatchingPreset = useCallback((fieldIds, currentValues) => {
    return presets.find(preset => {
      // Check if preset covers all the specified fields
      const coversFields = fieldIds.every(fieldId =>
        preset.includedFields.includes(fieldId)
      );
      if (!coversFields) return false;

      // Check if values match for included fields
      return preset.includedFields.every(fieldId => {
        const presetValue = preset.values[fieldId];
        const currentValue = currentValues[fieldId];

        // Handle GPS coordinates with tolerance
        const field = EXIF_FIELDS[fieldId];
        if (field?.type === 'gps-coordinate' && presetValue != null && currentValue != null) {
          return Math.abs(presetValue - currentValue) < 0.0001;
        }

        return presetValue === currentValue;
      });
    });
  }, [presets]);

  // Legacy compatibility: get camera presets
  const cameraPresets = presets
    .filter(p => p.includedFields.includes('cameraMake') || p.includedFields.includes('cameraModel'))
    .map(p => ({
      id: p.id,
      name: p.name,
      cameraMake: p.values.cameraMake || '',
      cameraModel: p.values.cameraModel || ''
    }));

  // Legacy compatibility: get lens presets
  const lensPresets = presets
    .filter(p => p.includedFields.includes('lensMake') || p.includedFields.includes('lensModel'))
    .map(p => ({
      id: p.id,
      name: p.name,
      lensModel: p.values.lensModel || '',
      lensMake: p.values.lensMake || ''
    }));

  // Legacy compatibility: get GPS presets
  const gpsPresets = presets
    .filter(p => p.includedFields.includes('latitude') || p.includedFields.includes('longitude'))
    .map(p => ({
      id: p.id,
      name: p.name,
      latitude: p.values.latitude,
      longitude: p.values.longitude
    }));

  // Legacy compatibility: camera preset operations
  const addCameraPreset = useCallback((preset) => {
    return addPreset(preset.name, ['cameraMake', 'cameraModel'], {
      cameraMake: preset.cameraMake || '',
      cameraModel: preset.cameraModel || ''
    });
  }, [addPreset]);

  const updateCameraPreset = useCallback((id, preset) => {
    updatePreset(id, {
      name: preset.name,
      includedFields: ['cameraMake', 'cameraModel'],
      values: {
        cameraMake: preset.cameraMake || '',
        cameraModel: preset.cameraModel || ''
      }
    });
  }, [updatePreset]);

  const deleteCameraPreset = useCallback((id) => {
    deletePreset(id);
  }, [deletePreset]);

  // Legacy compatibility: lens preset operations
  const addLensPreset = useCallback((preset) => {
    return addPreset(preset.name, ['lensMake', 'lensModel'], {
      lensModel: preset.lensModel || '',
      lensMake: preset.lensMake || ''
    });
  }, [addPreset]);

  const updateLensPreset = useCallback((id, preset) => {
    updatePreset(id, {
      name: preset.name,
      includedFields: ['lensMake', 'lensModel'],
      values: {
        lensModel: preset.lensModel || '',
        lensMake: preset.lensMake || ''
      }
    });
  }, [updatePreset]);

  const deleteLensPreset = useCallback((id) => {
    deletePreset(id);
  }, [deletePreset]);

  // Legacy compatibility: GPS preset operations
  const addGpsPreset = useCallback((preset) => {
    return addPreset(preset.name, ['latitude', 'longitude'], {
      latitude: preset.latitude,
      longitude: preset.longitude
    });
  }, [addPreset]);

  const updateGpsPreset = useCallback((id, preset) => {
    updatePreset(id, {
      name: preset.name,
      includedFields: ['latitude', 'longitude'],
      values: {
        latitude: preset.latitude,
        longitude: preset.longitude
      }
    });
  }, [updatePreset]);

  const deleteGpsPreset = useCallback((id) => {
    deletePreset(id);
  }, [deletePreset]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    presets,
    getAllPresets,
    getPresetById,
    getPresetsForFields,
    getPresetsForGroup,
    addPreset,
    updatePreset,
    deletePreset,
    findMatchingPreset,
    // Legacy compatibility
    cameraPresets,
    lensPresets,
    gpsPresets,
    addCameraPreset,
    updateCameraPreset,
    deleteCameraPreset,
    addLensPreset,
    updateLensPreset,
    deleteLensPreset,
    addGpsPreset,
    updateGpsPreset,
    deleteGpsPreset
  }), [
    presets,
    cameraPresets,
    lensPresets,
    gpsPresets,
    getAllPresets,
    getPresetById,
    getPresetsForFields,
    getPresetsForGroup,
    addPreset,
    updatePreset,
    deletePreset,
    findMatchingPreset,
    addCameraPreset,
    updateCameraPreset,
    deleteCameraPreset,
    addLensPreset,
    updateLensPreset,
    deleteLensPreset,
    addGpsPreset,
    updateGpsPreset,
    deleteGpsPreset
  ]);

  return (
    <PresetsContext.Provider value={value}>
      {children}
    </PresetsContext.Provider>
  );
}

export function usePresets() {
  const context = useContext(PresetsContext);
  if (!context) {
    throw new Error('usePresets must be used within PresetsProvider');
  }
  return context;
}
